import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { toast } from '@/lib/toast'
import type { 
  User, 
  UsersResponse, 
  CreateUserBody, 
  UpdateUserBody,
  Plan,
  CreatePlanBody,
  UpdatePlanBody
} from '@/types'

// Query keys
export const adminQueryKeys = {
  all: ['admin'] as const,
  users: () => [...adminQueryKeys.all, 'users'] as const,
  plans: () => [...adminQueryKeys.all, 'plans'] as const,
  templates: () => [...adminQueryKeys.all, 'templates'] as const,
} as const

// Users query
export const useUsers = (options = {}) => {
  return useQuery({
    queryKey: adminQueryKeys.users(),
    queryFn: async () => {
      const response = await api.get<any>('/admin/users')
      // Handle both response formats: { users: [] } or direct array
      if (response.data && typeof response.data === 'object' && 'users' in response.data) {
        return response.data.users
      }
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

// Create user mutation
export const useCreateUser = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: CreateUserBody) => {
      const response = await api.post<User>('/admin/users', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users() })
      toast.success('admin.userCreatedSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to create user')
    },
  })
}

// Update user mutation
export const useUpdateUser = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateUserBody }) => {
      // Backend expects query parameters, not request body
      const params = new URLSearchParams()
      if (data.first_name !== undefined) params.append('first_name', data.first_name)
      if (data.last_name !== undefined) params.append('last_name', data.last_name)
      if (data.email !== undefined) params.append('email', data.email)
      if (data.role !== undefined) params.append('role', data.role)
      if (data.is_active !== undefined) params.append('is_active', String(data.is_active))
      if (data.plan_id !== undefined) params.append('plan_id', String(data.plan_id))
      
      const response = await api.put<User>(`/admin/users/${id}?${params.toString()}`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users() })
      toast.success('admin.userUpdatedSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to update user')
    },
  })
}

// Delete user mutation
export const useDeleteUser = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/users/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users() })
      toast.success('admin.userDeletedSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to delete user')
    },
  })
}

// Delete user by email mutation
export const useDeleteUserByEmail = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (email: string) => {
      await api.delete(`/admin/users/email/${email}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users() })
      toast.success('admin.userDeletedSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to delete user')
    },
  })
}

// Plans query
export const usePlans = (options = {}) => {
  return useQuery({
    queryKey: adminQueryKeys.plans(),
    queryFn: async () => {
      const response = await api.get<Plan[]>('/admin/plans')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

// Create plan mutation
export const useCreatePlan = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: CreatePlanBody) => {
      const response = await api.post<Plan>('/admin/plans', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.plans() })
      toast.success('admin.planCreatedSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to create plan')
    },
  })
}

// Update plan mutation
export const useUpdatePlan = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdatePlanBody }) => {
      const response = await api.put<Plan>(`/admin/plans/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.plans() })
      toast.success('admin.planUpdatedSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to update plan')
    },
  })
}

// Delete plan mutation
export const useDeletePlan = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/plans/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.plans() })
      toast.success('admin.planDeletedSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to delete plan')
    },
  })
}

// Admin templates query
export const useAdminTemplates = (options = {}) => {
  return useQuery({
    queryKey: adminQueryKeys.templates(),
    queryFn: async () => {
      const response = await api.get<{ content: unknown }>('/admin/templates')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

// Change user password mutation (admin)
export const useChangeUserPassword = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, new_password }: { id: string; new_password: string }) => {
      const response = await api.put(`/admin/users/${id}/password`, { new_password })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users() })
      toast.success('admin.userPasswordChangedSuccess')
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to change user password')
    },
  })
}

// Backfill security log countries mutation (admin)
export const useBackfillSecurityLogCountries = () => {
  return useMutation({
    mutationFn: async (limit?: number) => {
      const params = limit ? `?limit=${limit}` : ''
      const response = await api.post<{
        message: string
        statistics: {
          total_records: number
          unique_ips: number
          updated: number
          skipped: number
          errors: number
        }
      }>(`/admin/security/backfill-countries${params}`)
      return response.data
    },
    onSuccess: (data) => {
      const stats = data.statistics
      toast.success(
        `Backfill completed: ${stats.updated} records updated, ${stats.skipped} skipped, ${stats.errors} errors`
      )
    },
    onError: (error: any) => {
      toast.raw.error(error.response?.data?.detail || 'Failed to backfill countries')
    },
  })
}